import * as React from "react";
import { useCallback, useState } from "react";
import styled from "@mui/material/styles/styled";
import { RefineFeaturePanel } from "../refineFeature/refineFeaturePanel";
import {
  PatientSuggestionsPerDiseaseMetrics,
  QualifiedFeature,
  Qualifier,
} from "fe-common/graphql";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { Button } from "@mui/material";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { PatientCaseOp, usePatientCase } from "../usePatientCase";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useScreenSize } from "../util/useScreenSize";
import { getQFAndRefinementsLines } from "fe-common/logic";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { SuggestionDiseaseStrength } from "../hooks/useSuggestions";
import { DataInput } from "../../dataInputTypes";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";

//region [[ Styles ]]

const RefineFeatureInlineView = styled("div")(({ theme }) => ({
  border: "1px solid " + KahunColorMap.DefaultLight,
  borderRadius: "9px",
  position: "relative",
  width: "100%",
  padding: "10px",
}));

const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
}));

const Title = styled("div")(({ theme }) => ({
  fontSize: "14px",
  padding: "10px 16px",
  color: "black",
}));

const HeaderContainer = styled("div")(({ theme }) => ({}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "10px",
  top: "10px",
  [theme.breakpoints.down(MobileView)]: {
    right: "10px",
  },
}));

const CloseIconButton = styled(CloseIcon)(({ theme }) => ({
  color: KahunColorMap.DefaultLight,
  fontSize: "24px",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: "capitalize",
  boxShadow: "none",
  fontSize: "16px",
  width: "110px",
  marginLeft: "15px",
  marginRight: "15px",
  marginBottom: "10px",
  borderRadius: "25px",
  backgroundColor: KahunColorMap.Primary,
  "&:hover": {
    backgroundColor: KahunColorMap.Primary,
    boxShadow: "none",
  },
  color: "white",

  [theme.breakpoints.down(MobileView)]: {
    width: "110px",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface RefineFeatureInlineProps {
  qf: QualifiedFeature;
  patientCaseGrouping: PatientCaseGrouping;
  response: DataInput;
  handleDone: () => void;
  causedByDisease?: (PatientSuggestionsPerDiseaseMetrics &
    SuggestionDiseaseStrength)[];
  relationId: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const RefineFeatureInline = ({ ...props }: RefineFeatureInlineProps) => {
  const [refineQf, setRefineQf] = useState(props.qf ? props.qf : null);
  const [change, setChange] = useState(null);
  const { doChangesToPending, doChangesToPatientCase } = usePatientCase();
  const { isMobile } = useScreenSize();
  let [findingTitle] = getQFAndRefinementsLines(props.qf);
  const { setNextRouteNavigationDisabled } = useDiagnosticNavigation();
  const onChange = useCallback(
    (qualifiers: Qualifier[]) => {
      setRefineQf({
        ...refineQf,
        qualifiers: qualifiers,
      });

      if (qualifiers.filter((q) => q.value).length === 0) {
        console.log("QF missing error: ", refineQf);
        console.log("response: ", props.response);
        console.log("relationId: ", props.relationId);

        setChange({
          op: PatientCaseOp.replaceQFInSection,
          qf: {
            ...refineQf,
            qualifiers: props.response.qualifiers.filter(
              (q) => q.relation.id !== props.relationId,
            ),
          },
          section:
            props.patientCaseGrouping || PatientCaseGrouping.CLINICAL_FEATURES,
        });
      } else {
        setChange({
          op: PatientCaseOp.updateQfInSection,
          qf: {
            ...refineQf,
            qualifiers,
          },
          section:
            props.patientCaseGrouping || PatientCaseGrouping.CLINICAL_FEATURES,
        });
      }
    },
    [props.patientCaseGrouping, refineQf, props.relationId, props.response],
  );

  const handleDone = (e) => {
    evt({
      category: CATEGORY.REFINE_POPUP,
      action: ACTION.REFINE_POPUP_DONE,
      label: refineQf.name,
    });

    if (change) {
      if (isMobile) {
        doChangesToPatientCase([change]);
        setNextRouteNavigationDisabled(false);
      } else {
        doChangesToPending([change]);
      }
    }

    if (props.handleDone) props.handleDone();
  };

  const handleCancel = (e) => {
    evt({
      category: CATEGORY.REFINE_POPUP,
      action: ACTION.REFINE_POPUP_CANCEL,
    });

    if (props.handleDone) props.handleDone();
  };

  return (
    <RefineFeatureInlineView>
      <HeaderContainer>
        <Title>{findingTitle}</Title>
        <CloseButton onClick={handleCancel} size={"small"}>
          <CloseIconButton />
        </CloseButton>
      </HeaderContainer>
      <RefineFeaturePanel
        showTitle={false}
        onChange={onChange}
        selectedQualifiers={refineQf?.qualifiers}
        qf={refineQf}
        showDuration={false}
        causedByDisease={props.causedByDisease}
      />
      <Actions>
        <ActionButton
          onClick={handleDone}
          color={"primary"}
          variant={"contained"}
        >
          Done
        </ActionButton>
      </Actions>
    </RefineFeatureInlineView>
  );
};
